<template>
  <v-form ref="form" v-if="!forApprovalDashboard || sharedLogLoaded">
    <v-card flat style="position: relative">
      <div class="close-btn">
        <v-btn @click="cancel" icon>
          <v-icon>close</v-icon>
        </v-btn>
      </div>
      <v-card-text class="clearfix" :class="isMobile ? 'px-0' : ''">
        <div ref="top-right" :class="rightColumnClass">
          <v-card flat class="pa-4">
            <v-layout align-center class="green--text mb-4">
              <h3>Logbook</h3>
            </v-layout>

            <InternshipSelect
              v-model="logEntry.internship"
              :showSpecialty="displaySpecialty"
              :specialty.sync="logEntry.specialty"
              :hint="displayHint"
            />
            <span class="caption grey--text text--darken-1"
              >Tipo de Registo</span
            >
            <v-select
              v-model="logEntry.logbook"
              item-value="id"
              item-text="name"
              :items="logbooks"
              no-data-text="A carregar, aguarde..."
              :loading="
                fetchingSpecialty || loadingLogbooks || logbooks.length === 0
              "
              clearable
              solo
              :disabled="disabled || fetchingSpecialty"
              class="thin-border"
            />
          </v-card>
        </div>
        <div v-if="logEntry.logbook" class="left-column">
          <v-card flat class="pa-4">
            <v-layout
              v-if="contentLoading"
              style="justify-content: center"
              class="my-5"
              color="primary"
              dark
            >
              <v-progress-circular
                indeterminate
                color="primary"
                :size="70"
              ></v-progress-circular>
            </v-layout>

            <div v-if="logEntry.logbook">
              <v-layout align-center class="green--text mb-4">
                <Icon name="heart" class="mr-2" />
                <h2>{{ selectedLogbook.name }}</h2>
              </v-layout>
              <v-layout wrap justify-space-between>
                <template v-for="component in components">
                  <v-flex
                    v-if="
                      component.type !== 'subcomponent' &&
                      checkConditions(component)
                    "
                    :key="component.id"
                    v-bind="size(component)"
                    class="px-2"
                    :class="component.id"
                    style="text-align: left"
                  >
                    <div
                      v-if="
                        ['single-select', 'multi-select'].includes(
                          component.type
                        )
                      "
                    >
                      <span class="caption grey--text text--darken-1">{{
                        component.label
                      }}</span>
                      <v-select
                        v-model="logEntry[component.id]"
                        clearable
                        :multiple="component.type === 'multi-select'"
                        :items="component.options || []"
                        @click:clear="() => (logEntry[component.id] = '')"
                        solo
                        class="thin-border"
                        style="width: 100%"
                      />
                    </div>

                    <AutoCompleteTree
                      v-if="
                        ['single-select-tree', 'multi-select-tree'].includes(
                          component.type
                        )
                      "
                      :singleSelect="
                        component.type === 'single-select-tree' ? true : false
                      "
                      v-model="logEntry[component.id]"
                      :optionsTree="getTreeItems(component)"
                      :component="component"
                      :name="component.label"
                    />

                    <Autocomplete
                      v-else-if="component.type === 'autocomplete'"
                      :component="component"
                      v-model="logEntry[component.id]"
                    />
                    <CustomSelect
                      v-else-if="
                        ['custom-select', 'custom-multi-select'].includes(
                          component.type
                        )
                      "
                      :multiple="component.type === 'custom-multi-select'"
                      v-model="logEntry[component.id]"
                      :label="component.label"
                      :options="component.options || []"
                    />
                    <HintField
                      v-else-if="
                        component.type === 'hint-field' ||
                        component.type === 'hint-field-multiple'
                      "
                      :componentId="component.id"
                      :label="component.label"
                      v-model="logEntry[component.id]"
                      :isMultiple="component.type === 'hint-field-multiple'"
                      :options="component.options || []"
                    />
                    <div
                      v-else-if="
                        component.type === 'text-field' ||
                        component.type === 'numeric-field'
                      "
                    >
                      <span class="caption grey--text text--darken-1">{{
                        component.label
                      }}</span>
                      <v-text-field
                        v-model="logEntry[component.id]"
                        :type="
                          component.type === 'numeric-field' ? 'number' : ''
                        "
                        solo
                        class="thin-border"
                      />
                    </div>
                    <AgeInput
                      v-else-if="component.type === 'age-field'"
                      :age.sync="logEntry[component.id]"
                    />
                    <v-checkbox
                      v-else-if="component.type === 'checkbox'"
                      v-model="logEntry[component.id]"
                      :label="component.label"
                    />
                    <div v-else-if="component.type === 'text-area'">
                      <span class="caption grey--text text--darken-1">{{
                        component.label
                      }}</span>
                      <v-textarea
                        v-model="logEntry[component.id]"
                        solo
                        class="thin-border"
                      />
                    </div>
                    <DatePicker
                      v-else-if="component.type === 'date-picker'"
                      :label="component.label"
                      :max="new Date().toISOString().substring(0, 10)"
                      v-model="logEntry[component.id]"
                    />
                    <DrugPicker
                      v-else-if="component.id === 'drugs'"
                      v-model="logEntry[component.id]"
                      :label="component.label"
                    />
                    <SurgicalAutonomy
                      v-else-if="component.type === 'surgical-autonomy'"
                      v-model="logEntry[component.id]"
                      :label="component.label"
                      :automy-components="
                        components.filter(c => c.type === 'autonomy')
                      "
                    />
                    <Autonomy
                      v-else-if="component.type === 'autonomy'"
                      v-model="logEntry[component.id]"
                      :label="component.label"
                      :autonomy-id="component.id"
                      :selected-autonomy="logEntry['surgical-autonomy']"
                    />
                    <!-- <SupervisorCombobox
                      v-else-if="component.id === 'supervisor'"
                      v-model="logEntry.supervisor"
                      :assistants-only="true"
                    /> -->
                    <AnesthesiaProcedure
                      v-else-if="component.id === 'anesthesia-procedure'"
                      :general-anesthesia.sync="logEntry['general-anesthesia']"
                      :combined-anesthesia.sync="
                        logEntry['combined-anesthesia']
                      "
                      :loco-regional-anesthesia.sync="
                        logEntry['loco-regional-anesthesia']
                      "
                      :peripheral-nerve-description.sync="
                        logEntry['periferal-nerve-description']
                      "
                      :cam.sync="logEntry['cam']"
                      :sedoanalgesia.sync="logEntry['sedoanalgesia']"
                      :guidance.sync="logEntry['anesthesia-guidance']"
                    />
                    <LocoRegionalTree
                      v-else-if="component.type === 'loco-regional-tree'"
                      :loco-regional-values.sync="logEntry[component.id]"
                    />
                    <AnesthesiaAppointmentType
                      v-else-if="
                        component.id === 'appointment-type' &&
                        component.type === 'custom'
                      "
                      v-model="logEntry['appointment-type']"
                      :notes.sync="logEntry['multi-diciplinary-notes']"
                    />

                    <ClavienDindo
                      v-else-if="component.id === 'complications-clavien-dindo'"
                      v-model="logEntry['complications-clavien-dindo']"
                    />
                    <SurgeryDiagnosisComponent
                      v-else-if="component.id === 'surgery-diagnosis'"
                      v-model="logEntry['surgery-diagnosis']"
                      :qualifiers.sync="
                        logEntry['surgery-diagnosis-qualifiers']
                      "
                      @update:region="logEntry['anatomic-region'] = $event"
                      :malignancy.sync="
                        logEntry['classification-benign-suspicious-malignant']
                      "
                      :surgery-diagnosis-other.sync="
                        logEntry['surgery-diagnosis-other']
                      "
                      :region="logEntry['anatomic-region']"
                      @update:malignancy="logEntry['p-tnm-staging'] = null"
                    />
                    <v-text-field
                      v-else-if="
                        component.id === 'p-tnm-staging' &&
                        logEntry[
                          'classification-benign-suspicious-malignant'
                        ] === 'malignant'
                      "
                      v-model="logEntry[component.id]"
                      :label="component.label"
                    />
                    <SurgeryProceduresComponent
                      v-else-if="component.id === 'surgery-procedure'"
                      v-model="logEntry['surgery-procedure']"
                      :surgery-procedure-other.sync="
                        logEntry['surgery-procedure-other']
                      "
                      @update:procedure-ids="
                        logEntry['surgery-procedure-ids'] = $event
                      "
                    />
                    <!-- <SurgeryAutonomy
                      v-else-if="component.id === 'surgery-autonomy'"
                      :autonomy.sync="logEntry.autonomy"
                      :surgeon.sync="logEntry.surgeon"
                      :helper1.sync="logEntry['helper-1']"
                      :helper2.sync="logEntry['helper-2']"
                      :helper3.sync="logEntry['helper-3']"
                      :observer.sync="logEntry.observer"
                    />-->
                    <ICPCPicker
                      v-else-if="component.id === 'diagnosis-icpc'"
                      v-model="logEntry['diagnosis-icpc']"
                      name="Diagnósticos ICPC"
                      :enabledICPCComponents="[
                        'signs-and-symptoms',
                        'diseases'
                      ]"
                    />
                    <BloodPressure
                      v-else-if="component.id === 'blood-pressure'"
                      :diagnosesICPC="logEntry['diagnosis-icpc']"
                      :systolicBloodPressure.sync="
                        logEntry['systolicBloodPressure']
                      "
                      :diastolicBloodPressure.sync="
                        logEntry['diastolicBloodPressure']
                      "
                      :controledBloodPressure.sync="
                        logEntry['controledBloodPressure']
                      "
                    />
                    <GlycosylatedHemoglobin
                      v-else-if="component.id === 'a1c'"
                      :a1c.sync="logEntry['a1c']"
                      :controlledA1c.sync="logEntry['controlledA1c']"
                      :diagnosesICPC="logEntry['diagnosis-icpc']"
                    />
                    <Albuminuria
                      v-else-if="component.id === 'albuminuria'"
                      :albuminuria.sync="logEntry['albuminuria']"
                      :diagnosesICPC="logEntry['diagnosis-icpc']"
                    />
                    <FileUpload
                      v-else-if="component.type === 'image-upload'"
                      :file-url-uploaded.sync="logEntry[component.id + '-url']"
                      :file-name-uploaded.sync="
                        logEntry[component.id + '-name']
                      "
                      :path-to-files="filepath"
                      acceptedType="image/*"
                      label="Anexar imagem"
                    ></FileUpload>
                    <AnesthesiaDuration
                      v-else-if="component.id === 'duration'"
                      :durationHours.sync="logEntry.durationHours"
                      :durationMinutes.sync="logEntry.durationMinutes"
                    />
                    <NeuroradiologyCurrentEpisode
                      :logEntry="logEntry"
                      v-else-if="
                        component.id === 'current-episode' &&
                        logEntry['intervention-type'] !== 'dacryocystography'
                      "
                      :component="component"
                      @onUpdateData="updateNeuroRadiologyData($event)"
                    />

                    <OrthopedicDiagnosis
                      v-else-if="component.id === 'orthopedic-diagnosis'"
                      v-model="logEntry['orthopedic-diagnosis']"
                      :typeOfSurgery="logEntry['type-of-surgery']"
                      :anatomicRegion="logEntry['anatomic-region']"
                      :orthopedic-diagnosis-other.sync="
                        logEntry['orthopedic-diagnosis-other']
                      "
                      @update:diagnosis-id="
                        logEntry['orthopedic-diagnosis-id'] = $event
                      "
                      @update:anatomic-region-id="
                        logEntry['anatomic-region'] = $event
                      "
                    />

                    <OrthopedicProcedures
                      v-else-if="component.id === 'orthopedic-procedures'"
                      v-model="logEntry['orthopedic-procedures']"
                      :typeOfSurgery="logEntry['type-of-surgery']"
                      :anatomicRegion="logEntry['anatomic-region']"
                      :orthopedic-diagnosis-other.sync="
                        logEntry['orthopedic-procedure-other']
                      "
                      @update:procedures-ids="
                        logEntry['orthopedic-procedures-ids'] = $event
                      "
                    />

                    <DiagnosisTopographyICDO
                      v-else-if="component.type === 'icdo-topography'"
                      :component="component"
                      v-model="logEntry[component.id]"
                    />

                    <DiagnosisHistologicICDO
                      v-else-if="component.type === 'icdo-histology'"
                      :component="component"
                      v-model="logEntry[component.id]"
                    />

                    <DiagnosisICD10
                      v-else-if="
                        ['icd10', 'icd10-sec'].includes(component.type)
                      "
                      :mainIcd10="component.type === 'icd10' ? true : false"
                      :component="component"
                      v-model="logEntry[component.id]"
                      :logbook="logEntry.logbook"
                    />

                    <DiagnosisICD11
                      v-else-if="
                        ['icd11', 'icd11-sec'].includes(component.type)
                      "
                      :component="component"
                      v-model="logEntry[component.id]"
                      :logbook="logEntry.logbook"
                      :maxEntries="component.type === 'icd11' ? 4 : 8"
                    />

                    <DiagnosisPatology
                      v-else-if="component.id === 'pathology-diagnosis'"
                      :label="component.label"
                      v-model="logEntry[component.id]"
                    />

                    <NeurosurgeryProcedure
                      v-else-if="
                        component.id === 'procedure' &&
                        component.type === 'custom'
                      "
                      v-model="logEntry[component.id]"
                      :label="component.label"
                      :diagnosisSelected="logEntry.diagnosis"
                    />
                    <OrganSystemSelector
                      v-else-if="
                        component.id === 'organ-system-i' &&
                        component.type === 'custom'
                      "
                      v-model="logEntry[component.id]"
                      :component="component"
                      :diagnosis="selectedDiagnosis"
                    />
                    <OrganSystem2Selector
                      v-else-if="
                        component.id === 'organ-system-ii' &&
                        component.type === 'custom' &&
                        logEntry['organ-system-i']
                      "
                      v-model="logEntry[component.id]"
                      :component="component"
                      :diagnosis="selectedDiagnosis"
                      :organSystem="logEntry['organ-system-i']"
                    />
                    <DiagnosisSelector
                      v-else-if="
                        component.id === 'diagnosis' &&
                        component.type === 'custom'
                      "
                      v-model="logEntry[component.id]"
                      :component="component"
                      :organSystem="logEntry['organ-system-i']"
                      @diagnosisSelected="updateSelectedDiagnosis"
                    />

                    <MorphologicGroupSelector
                      v-else-if="
                        component.id === 'morphologic-group-i' &&
                        component.type === 'custom'
                      "
                      v-model="logEntry[component.id]"
                      :component="component"
                      :diagnosis="selectedDiagnosis"
                    />
                    <MorphologicGroup2Selector
                      v-else-if="
                        component.id === 'morphologic-group-ii' &&
                        component.type === 'custom'
                      "
                      v-model="logEntry[component.id]"
                      :component="component"
                      :diagnosis="selectedDiagnosis"
                    />
                    <CategorySelector
                      v-else-if="
                        component.id === 'category' &&
                        component.type === 'custom'
                      "
                      v-model="logEntry[component.id]"
                      :component="component"
                      :diagnosis="selectedDiagnosis"
                    />

                    <div style="border-left: 3px solid gainsboro">
                      <DynamicInput
                        v-for="conditionalComponent of conditionalComponents(
                          component
                        )"
                        :key="conditionalComponent.id"
                        :component="conditionalComponent"
                        :value="logEntry[conditionalComponent.id]"
                        @update-log="updateLogEntry"
                      />
                    </div>
                  </v-flex>
                </template>
              </v-layout>
            </div>
          </v-card>
        </div>
        <div
          ref="bottom-right"
          v-show="logEntry.logbook"
          class="right-column"
          :style="bottomRightColumn"
        >
          <v-card flat class="pa-4">
            <span class="caption grey--text text--darken-1">Estado:</span>
            <v-checkbox
              class="mt-0"
              v-model="logEntry.incompleted"
              label="Incompleto"
            />
            <CollegueFormInput
              v-if="!forApprovalDashboard"
              @updateUsers="updateUsers"
            />
          </v-card>
        </div>
      </v-card-text>
      <v-divider />
      <v-card-text>
        <div class="pl-2">
          <v-btn
            v-if="updateMode"
            color="secondary"
            :disabled="isSubmitValid"
            :loading="loading"
            @click="submit"
            depressed
            round
            >{{
              $t(
                this.logToEdit && this.logToEdit.id
                  ? 'common.Update'
                  : 'common.Submit'
              )
            }}</v-btn
          >
          <!--  <v-btn
          v-if="forApprovalDashboard"
          color="error"
          depressed
          @click="reject"
        >
          {{ $t('common.Reject') }}
        </v-btn> -->

          <v-btn color="error" round depressed @click="cancel">
            {{
              $t(
                this.updateMode && !this.forApprovalDashboard
                  ? 'common.Cancel'
                  : 'common.Close'
              )
            }}
          </v-btn>
        </div>
      </v-card-text>
    </v-card>
  </v-form>
</template>

<script>
import { mapActions } from 'vuex'
import FileUpload from '@/components/files/FileUpload'
import {
  Logbook,
  Component,
  LogbookComponentConditions,
  Internship
} from '../../../models/Specialty'
import AnesthesiaProcedure from './AnesthesiaProcedure'
import AnesthesiaAppointmentType from './AnesthesiaAppointmentType'
import LocoRegionalTree from './LocoRegionalTree'
import AnesthesiaDuration from './AnesthesiaDuration'
import ClavienDindo from './ClavienDindo'
//import SurgeryAutonomy from './SurgeryAutonomy'
import InternshipSelect from './InternshipSelect'
import Autocomplete from '@/components/inputs/Autocomplete'
import HintField from './HintField'
import SurgeryDiagnosisComponent from './SurgeryDiagnosis'
import SurgeryProceduresComponent from './SurgeryProcedures'
import DatePicker from '../../../components/inputs/DatePicker'
import AgeInput from '@/components/inputs/AgeInput'
import CustomSelect from '@/components/inputs/CustomSelect'
import ICPCPicker from './ICPCPicker'
import DrugPicker from './DrugPicker'
import Autonomy from './Autonomy'
import SurgicalAutonomy from './SurgicalAutonomy'
import BloodPressure from './BloodPressure'
import GlycosylatedHemoglobin from './GlycosylatedHemoglobin'
import Albuminuria from './Albuminuria'
import NeuroradiologyCurrentEpisode from './NeuroradiologyCurrentEpisode'
import OrthopedicDiagnosis from './OrthopedicDiagnosis'
import OrthopedicProcedures from './OrthopedicProcedures'
import DiagnosisTopographyICDO from './DiagnosisTopographyICDO'
import DiagnosisHistologicICDO from './DiagnosisHistologicICDO'
import DiagnosisICD10 from './DiagnosisICD10'
import DiagnosisICD11 from './DiagnosisICD11'
import DiagnosisPatology from './DiagnosisPatology'
import NeurosurgeryProcedure from './NeurosurgeryProcedure'
import { TreeDataDictionary } from '../../../models/Specialty'
import AutoCompleteTree from './AutocompleteTree'
import CollegueFormInput from './CollegueFormInput'
import specialtyFetcher from '../../../mixins/specialtyFetcher'
import DynamicInput from './DynamicInput.vue'
import OrganSystemSelector from './OrganSystemSelector.vue'
import OrganSystem2Selector from './OrganSystem2Selector.vue'
import CategorySelector from './CategorySelector.vue'
import DiagnosisSelector from './DiagnosisSelector.vue'
import MorphologicGroupSelector from './MorphologicGroupSelector.vue'
import MorphologicGroup2Selector from './MorphologicGroup2Selector.vue'

export default {
  components: {
    AnesthesiaProcedure,
    AnesthesiaAppointmentType,
    LocoRegionalTree,
    AnesthesiaDuration,
    ClavienDindo,
    InternshipSelect,
    DatePicker,
    DrugPicker,
    Autonomy,
    SurgeryDiagnosisComponent,
    SurgeryProceduresComponent,
    AgeInput,
    CustomSelect,
    ICPCPicker,
    BloodPressure,
    GlycosylatedHemoglobin,
    HintField,
    Albuminuria,
    NeuroradiologyCurrentEpisode,
    Autocomplete,
    OrthopedicDiagnosis,
    OrthopedicProcedures,
    DiagnosisTopographyICDO,
    DiagnosisHistologicICDO,
    DiagnosisICD10,
    DiagnosisICD11,
    DiagnosisPatology,
    AutoCompleteTree,
    NeurosurgeryProcedure,
    CollegueFormInput,
    DynamicInput,
    FileUpload,
    OrganSystemSelector,
    OrganSystem2Selector,
    CategorySelector,
    DiagnosisSelector,
    MorphologicGroupSelector,
    MorphologicGroup2Selector,
    SurgicalAutonomy
  },
  props: {
    logToEdit: { type: Object, default: null },
    internship: { type: String },
    close: { type: Function, required: true },
    returnlogbook: { type: Function, required: false },
    contentLoading: { type: Boolean, default: false },
    updateMode: { type: Boolean, default: true },
    forApprovalDashboard: { type: Boolean, default: false },
    internshipMandatory: { type: Boolean, default: false },
    afterSubmit: { type: Function, required: false },
    afterReject: { type: Function, required: false },
    expandDialog: { type: Boolean },
    selectedSpecialty: { type: String, default: null }
  },
  mixins: [specialtyFetcher],
  data() {
    return {
      selectedDiagnosis: null,
      logEntry: {
        userId: this.$store.getters.profile.id,
        specialty: this.$store.getters.specialty,
        logbook: null,
        incompleted: null
      },
      loading: false,
      asa: '',
      disabled: false,
      displayHint: true,
      selectedUsers: [],
      sharedLogLoaded: false,
      loadingLogbooks: false,
      fetchingSpecialty: false
    }
  },
  computed: {
    filepath() {
      return `${this.$store.getters.profile.id}/logbook-images`
    },
    logbooks() {
      return Logbook.query().get()
    },
    selectedInternship: {
      get() {
        return this.internship
      },
      set(internship) {
        this.$emit('update:internship', internship)
      }
    },
    specialty() {
      return this.selectedSpecialty || this.$store.getters.specialty
    },
    displaySpecialty() {
      return this.specialty === 'anesthesiology'
    },
    selectedLogbook() {
      return (
        Logbook.query()
          .withAllRecursive()
          .where('id', this.logEntry.logbook)
          .first() || {}
      )
    },
    components() {
      return Component.query()
        .withAllRecursive()
        .get()
        .filter(component =>
          component.logbooks.includes(this.selectedLogbook.id)
        )
    },
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown
    },
    isSubmitValid() {
      return (
        !this.logEntry.logbook || (this.internshipMandatory && !this.internship)
      )
    },
    rightColumnClass() {
      const mainClass = this.logEntry.logbook ? 'right-column' : ''
      if (!this.isMobile) {
        return [mainClass, 'sticky']
      }
      return [mainClass]
    },
    bottomRightColumn() {
      const isSticky = this.isMobile ? '' : 'sticky'
      return { position: isSticky }
    }
  },
  created() {
    if (this.logToEdit) {
      this.disabled = true
      this.displayHint = false
      if (!this.forApprovalDashboard) {
        this.$root.$emit('update:internship', this.logToEdit.internship)
      }
      this.logEntry = {
        ...this.logToEdit,
        userId: this.$store.getters.profile.id,
        specialty: this.logToEdit.specialty
      }
      this.$emit('update:expandDialog', true)
    }
  },
  mounted() {
    if (this.forApprovalDashboard) {
      this.logEntry.internship = this.internship
      this.setLogs([])
      this.fetchSpecialty(this.specialty).then(
        function () {
          this.getLogs()
            .catch(err => {
              // eslint-disable-next-line no-console
              console.error(err)
              this.showErrorSnackbar('Something went wrong')
            })
            .finally(() => {
              this.sharedLogLoaded = true
            })
        }.bind(this)
      )
    }
  },
  methods: {
    ...mapActions([
      'setLogs',
      'getLogs',
      'addLog',
      'updateLog',
      'showSuccessSnackbar',
      'showErrorSnackbar',
      'deleteLogs',
      'getInternships'
    ]),
    updateSelectedDiagnosis(value) {
      this.selectedDiagnosis = value
    },
    checkConditions(component) {
      if (component.conditionalOptions.length) {
        return false
      }

      const conditions = LogbookComponentConditions.query()
        .where('componentId', component.id)
        .get()

      if (conditions && conditions.length) {
        if (this.applyConditions(conditions)) {
          return true
        } else {
          return false
        }
      } else {
        return true
      }
    },
    applyConditions(conditions) {
      for (let cIndex = 0; cIndex < conditions.length; cIndex++) {
        const c = conditions[cIndex]

        if (this.applyCondition(c)) {
          return true
        }
      }

      return false
    },
    applyCondition(condition) {
      const conditionalComponentValue =
        this.logEntry[condition.conditionalComponentId]

      if (Array.isArray(conditionalComponentValue)) {
        return conditionalComponentValue.some(ccv =>
          condition.conditionalComponentOptionsIds.includes(ccv)
        )
      } else {
        return condition.conditionalComponentOptionsIds.includes(
          conditionalComponentValue
        )
      }
    },
    getTreeItems(component) {
      const tree = TreeDataDictionary.query()
        .where('tabName', component.tabName)
        .first()
      return tree.treeData || []
    },
    cancel() {
      this.logEntry = {
        userId: this.$store.getters.profile.id,
        specialty: this.$store.getters.user.specialty,
        logbook: this.logEntry.logbook
      }
      this.close()
    },
    updateNeuroRadiologyData(neuroradiologyData) {
      this.logEntry = {
        ...this.logEntry,
        ...neuroradiologyData
      }
    },
    size(component) {
      let size = {
        xs: 'md2',
        sm: 'md3',
        md: 'md6',
        lg: 'md8',
        xl: 'md10',
        xxl: 'md12'
      }[component.size]

      let sizeMobile = {
        xs: 'xs2',
        sm: 'xs3',
        md: 'xs5',
        lg: 'xs8',
        xl: 'xs10',
        xxl: 'xs12'
      }[component.sizeMobile]

      if (!size) {
        size = [
          'single-select-tree',
          'multi-select-tree',
          'custom',
          'icdo-topography',
          'icdo-histology',
          'icd10',
          'icd10-sec',
          'collegue-input',
          'pathology-diagnosis',
          'autonomy'
        ].includes(component.type)
          ? 'md12'
          : 'md6'
      }

      if (!sizeMobile) {
        sizeMobile = 'xs12'
      }

      return { [size]: true, [sizeMobile]: true }
    },
    updateLogEntry(payload) {
      const logEntry = { ...this.logEntry, ...payload }
      Object.keys(logEntry).forEach(key => {
        if (
          logEntry[key] === undefined ||
          (logEntry[key] instanceof Array && logEntry[key].length === 0)
        ) {
          delete logEntry[key]
        }
      })

      this.logEntry = { ...logEntry }
    },
    updateUsers(selectedUsers) {
      this.selectedUsers = selectedUsers
    },
    submit() {
      setTimeout(() => {
        if (this.returnlogbook) {
          this.returnlogbook(this.logEntry)
          this.close()
        } else {
          const action =
            this.logToEdit && this.logToEdit.id ? this.updateLog : this.addLog
          // TODO: Add action if update is during log csv import
          this.loading = true
          action({ ...this.logEntry })
            .then(log => {
              if (this.selectedUsers.length >= 1) {
                const sharedLog = log ? log : this.logEntry
                this.$root.$emit(
                  'addLogsToCollegues',
                  [sharedLog],
                  this.selectedUsers
                )
              }
            })
            .then(() => {
              if (this.afterSubmit) {
                this.afterSubmit()
              }
            })
            .catch(err => {
              // eslint-disable-next-line no-console
              console.error(err)
              this.showErrorSnackbar('Something went wrong')
            })
            .finally(() => {
              this.loading = false
              if (!this.logToEdit || this.forApprovalDashboard) {
                this.logEntry = {
                  userId: this.$store.getters.profile.id,
                  logbook: this.logEntry.logbook
                }
              }
              this.close()
            })
        }
      })
    },

    conditionalComponents(parentComponent) {
      const conditionalComponents = this.components.filter(component => {
        return parentComponent.options.some(
          option =>
            component.conditionalOptions.includes(option.value) &&
            Object.values(this.logEntry).includes(option.value)
        )
      })
      return conditionalComponents
    }
  },

  watch: {
    'logEntry.logbook'(val, old) {
      if (val !== old) {
        if (this.$refs['bottom-right']) {
          this.$refs['bottom-right'].style.top =
            this.$refs['top-right'].clientHeight + 'px'
        }
        this.$emit('update:expandDialog', true)
        this.$root.$emit('logbooktype', val)
      } else if (!val || !old) {
        this.$emit('update:expandDialog', false)
      }
    },
    async 'logEntry.internship'(val, old) {
      if (val !== old) {
        const specialtyInternships = Internship.query().get()

        const specialtyInternship = specialtyInternships.find(i => i.id === val)

        if (specialtyInternship) {
          this.logEntry.specialty = specialtyInternship.specialtyId
          this.fetchingSpecialty = true
          await this.fetchSpecialty(specialtyInternship.specialtyId)
          this.fetchingSpecialty = false
        }
      }
    }
  }
}
</script>

<style>
.left-column {
  float: none;
  width: 100%;
}
.right-column {
  float: none;
  width: 100%;
}
.right-column.sticky {
  position: sticky;
  top: 0;
}
@media (min-width: 960px) {
  .left-column {
    float: left;
    width: 60%;
  }
  .right-column {
    float: right;
    width: 40%;
  }
}

.clearfix:after {
  content: '.';
  display: block;
  clear: both;
  visibility: hidden;
  line-height: 0;
  height: 0;
}

.clearfix {
  display: inline-block;
}

html[xmlns] .clearfix {
  display: block;
}

* html .clearfix {
  height: 1%;
}
.close-btn {
  position: absolute;
  right: 0px;
  z-index: 1;
}
</style>
